/**
 * Phase i18n · Nymia locale bootstrap.
 *
 * Resolution priority (per product decision):
 *    1. Authenticated user → `users.preferred_language`
 *    2. Anonymous          → `localStorage['nymia_language']`
 *    3. No preference      → navigator.language / navigator.languages
 *    4. Unsupported        → 'it' fallback
 *
 * Missing translation keys fall back to English (the master resource),
 * so a partially-translated release still ships correctly.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import itCommon from './locales/it/common.json';
import itAuth from './locales/it/auth.json';
import itHeader from './locales/it/header.json';
import itSettings from './locales/it/settings.json';
import itLegal from './locales/it/legal.json';
import itCommunityRules from './locales/it/communityRules.json';
import itChildSafety from './locales/it/childSafety.json';
import itLanding from './locales/it/landing.json';
import itCreatorPage from './locales/it/creator_page.json';
import itWhatYouCanDo from './locales/it/whatYouCanDo.json';
import itHome from './locales/it/home.json';
import itExplore from './locales/it/explore.json';
import itCreate from './locales/it/create.json';
import itWallet from './locales/it/wallet.json';
import itShop from './locales/it/shop.json';
import itMessages from './locales/it/messages.json';
import itProfile from './locales/it/profile.json';
import itPrivateProfile from './locales/it/privateProfile.json';
import itO2O from './locales/it/o2o.json';
import itWaitingRoom from './locales/it/waiting_room.json';
import itDispute from './locales/it/dispute.json';
import itLives from './locales/it/lives.json';
import itAdmin from './locales/it/admin.json';
import itFaq from './locales/it/faq.json';
import itErrors from './locales/it/errors.json';
import itContact from './locales/it/contact.json';
import itBilling from './locales/it/billing.json';
import itOnboarding from './locales/it/onboarding.json';
import itPwa from './locales/it/pwa.json';
import itHelpCenter from './locales/it/helpCenter.json';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enHeader from './locales/en/header.json';
import enSettings from './locales/en/settings.json';
import enLegal from './locales/en/legal.json';
import enCommunityRules from './locales/en/communityRules.json';
import enChildSafety from './locales/en/childSafety.json';
import enLanding from './locales/en/landing.json';
import enCreatorPage from './locales/en/creator_page.json';
import enWhatYouCanDo from './locales/en/whatYouCanDo.json';
import enHome from './locales/en/home.json';
import enExplore from './locales/en/explore.json';
import enCreate from './locales/en/create.json';
import enWallet from './locales/en/wallet.json';
import enShop from './locales/en/shop.json';
import enMessages from './locales/en/messages.json';
import enProfile from './locales/en/profile.json';
import enPrivateProfile from './locales/en/privateProfile.json';
import enO2O from './locales/en/o2o.json';
import enWaitingRoom from './locales/en/waiting_room.json';
import enDispute from './locales/en/dispute.json';
import enLives from './locales/en/lives.json';
import enAdmin from './locales/en/admin.json';
import enFaq from './locales/en/faq.json';
import enErrors from './locales/en/errors.json';
import enContact from './locales/en/contact.json';
import enBilling from './locales/en/billing.json';
import enOnboarding from './locales/en/onboarding.json';
import enPwa from './locales/en/pwa.json';
import enHelpCenter from './locales/en/helpCenter.json';

export const SUPPORTED_LANGUAGES = ['it', 'en'];
export const DEFAULT_LANGUAGE = 'it';
export const STORAGE_KEY = 'nymia_language';

/**
 * Read the currently persisted preference for anonymous flows.
 * `AuthContext` will override this on login via `applyUserLanguage()`.
 */
const readInitialLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch (_) {
    /* localStorage unavailable — fall through to detector */
  }
  return null;
};

const resources = {
  it: {
    common: itCommon,
    auth: itAuth,
    header: itHeader,
    settings: itSettings,
    legal: itLegal,
    communityRules: itCommunityRules,
    childSafety: itChildSafety,
    landing: itLanding,
    creator_page: itCreatorPage,
    whatYouCanDo: itWhatYouCanDo,
    home: itHome,
    explore: itExplore,
    create: itCreate,
    wallet: itWallet,
    shop: itShop,
    messages: itMessages,
    profile: itProfile,
    privateProfile: itPrivateProfile,
    o2o: itO2O,
    waiting_room: itWaitingRoom,
    dispute: itDispute,
    lives: itLives,
    admin: itAdmin,
    faq: itFaq,
    errors: itErrors,
    contact: itContact,
    billing: itBilling,
    onboarding: itOnboarding,
    pwa: itPwa,
    helpCenter: itHelpCenter,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    header: enHeader,
    settings: enSettings,
    legal: enLegal,
    communityRules: enCommunityRules,
    childSafety: enChildSafety,
    landing: enLanding,
    creator_page: enCreatorPage,
    whatYouCanDo: enWhatYouCanDo,
    home: enHome,
    explore: enExplore,
    create: enCreate,
    wallet: enWallet,
    shop: enShop,
    messages: enMessages,
    profile: enProfile,
    privateProfile: enPrivateProfile,
    o2o: enO2O,
    waiting_room: enWaitingRoom,
    dispute: enDispute,
    lives: enLives,
    admin: enAdmin,
    faq: enFaq,
    errors: enErrors,
    contact: enContact,
    billing: enBilling,
    onboarding: enOnboarding,
    pwa: enPwa,
    helpCenter: enHelpCenter,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: readInitialLanguage() || undefined,   // undefined = let the detector run
    fallbackLng: {
      // Any unsupported browser locale falls back to Italian per product
      // decision. `en` still serves as a technical fallback for MISSING
      // keys — see `fallbackNS` + `saveMissing` disabled below.
      default: [DEFAULT_LANGUAGE],
    },
    supportedLngs: SUPPORTED_LANGUAGES,
    load: 'languageOnly',                        // en-US → en, it-IT → it
    ns: [
      'common', 'auth', 'header', 'settings', 'legal', 'communityRules', 'childSafety', 'landing', 'creator_page', 'whatYouCanDo',
      'home', 'explore', 'create', 'wallet', 'shop', 'messages', 'profile',
      'privateProfile',
      'o2o', 'waiting_room', 'dispute', 'lives', 'admin', 'faq', 'errors',
      'contact', 'billing', 'onboarding', 'pwa', 'helpCenter',
    ],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,   // React already escapes
    },
    detection: {
      // Order used ONLY at first paint; auth-aware sync is handled by
      // `applyUserLanguage` further down.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Apply a language coming from the authenticated user's profile.
 *
 * Also writes it to localStorage so subsequent anonymous visits keep the
 * same UI even before the profile finishes loading.
 */
export const applyUserLanguage = (lang) => {
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (_) {
    /* ignore */
  }
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
};

/**
 * Change the active language + persist locally. Callers should also
 * fire the backend PUT /api/auth/me/language for authenticated users
 * — see AuthContext#setLanguage.
 */
export const setLocalLanguage = (lang) => {
  applyUserLanguage(lang);
};

/**
 * Phase 42 · Keep the `<html lang>` attribute (and Content-Language meta,
 * when present) in sync with the runtime i18n language. Prevents mobile
 * browsers — most notably Chrome on Android — from prompting the user to
 * translate the page when it is already displayed in their language.
 *
 * The initial value is set right away on first paint; subsequent language
 * changes are observed via the i18next `languageChanged` event.
 */
const applyHtmlLang = (lang) => {
  if (typeof document === 'undefined' || !lang) return;
  const short = String(lang).split('-')[0];
  try {
    document.documentElement.setAttribute('lang', short);
    const meta = document.querySelector('meta[http-equiv="Content-Language"]');
    if (meta) meta.setAttribute('content', short);
  } catch (_e) {
    /* noop */
  }
};

// Sync on boot + on every language change.
applyHtmlLang(i18n.language);
i18n.on('languageChanged', applyHtmlLang);

export default i18n;
